import React, { useEffect, useState } from "react";
import { fetchMonthlyHoursWorked } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from '@mui/material/styles';
import { formatHoursMinutes } from "../utils/time";
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const MonthlyHoursWorkedChart = ({ date, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [workcellNames, setWorkcellNames] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  useEffect(() => {
    if (!date) {
      setData([]);
      setWorkcellNames({});
      setIsLoading(false);
      onLoadingChange?.(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchMonthlyHoursWorked(date)
      .then((apiData) => {
        if (cancelled) return;
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
          setWorkcellNames({});
          setData([]);
          return;
        }
        // Create a full month dataset with default 0 values for each workcell
        const workcells = [...new Set(apiData.map((entry) => entry.workcell_id))];
        const workcellNamesMap = {};
        apiData.forEach((entry) => {
          workcellNamesMap[entry.workcell_id] = entry.workcell_name;
        });
        setWorkcellNames(workcellNamesMap);

        const days = getMonthDays(date);
        const fullMonthData = days.map((day) => {
          const entry = { day };
          workcells.forEach((workcell) => { entry[workcell] = 0; });
          return entry;
        });

        // Merge API data into the full dataset
        apiData.forEach((entry) => {
          const index = fullMonthData.findIndex((d) => d.day === entry.day);
          if (index !== -1) {
            fullMonthData[index][entry.workcell_id] = entry.active_hours;
          }
        });

        setData(fullMonthData);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setWorkcellNames({});
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
        onLoadingChange?.(false);
      });
    return () => { cancelled = true; };
  }, [date]);

  // Sort workcells by total hours worked
  const sortedWorkcells = data.length > 0
    ? Object.keys(data[0] || {})
        .filter(key => key !== "day")
        .sort((a, b) => {
          const totalA = data.reduce((sum, entry) => sum + (entry[a] || 0), 0);
          const totalB = data.reduce((sum, entry) => sum + (entry[b] || 0), 0);
          return totalB - totalA;
        })
    : [];

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (data.length === 0) return <div>{t("common.noData")}</div>;

  // Custom tick for month name (only show for first day of month)
  const MonthTick = (props) => {
    const { x, y, payload, index } = props;
    const date = parseLocalDateTime(payload.value);
    const isFirst = index === 0;
    const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
    const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
    return isMonthStart ? (
      <g>
        <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
          {date.toLocaleString(locale, { month: 'long' })}
        </text>
      </g>
    ) : null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        {/* Main X axis: days */}
        <XAxis
          dataKey="day"
          tickFormatter={(tick) => parseLocalDateTime(tick)?.getDate() || ''}
          height={30}
          axisLine={true}
          tickLine={true}
        />
        {/* Secondary X axis: month name */}
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          height={20}
          xAxisId="month"
          tick={MonthTick}
          interval={0}
          allowDataOverflow={true}
          orientation="bottom"
        />
        <YAxis
          label={{
            value: t("monthly.hoursWorked.chart.axisLabel"),
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle" }
          }}
        />
      <Tooltip formatter={(value) => (typeof value === 'number' ? formatHoursMinutes(value) : value)} />
        {sortedWorkcells.map((workcell, index) => (
          <Bar
            key={workcell}
            dataKey={workcell}
            name={workcellNames[workcell]}
            stackId="a"
            fill={theme.palette.chart.colors[index % theme.palette.chart.colors.length]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default MonthlyHoursWorkedChart;
